import React from 'react';
import PropTypes from 'prop-types';
import { getCompoundSentenceList, getListItems } from '../../format';
import { SummaryItem } from './SummaryItem';
import {
  StyledConfirmationOrderSummary,
  OrderSummaryTitle,
  OngoingSubscriptionTitle,
  Section,
  SubsectionColumn,
  ExpectedDeliveryWrapper,
  SectionTitle,
  SectionSubtitle,
  SectionBody,
} from './styles.js';

const subscriptionfilters = {
  frequencyInWeeks: {
    label: 'Frequency',
    content: n => (n < 2 ? `Once a week` : `Every ${n} weeks`),
  },
  deliveryNext: { label: 'Next Delivery', content: date => date },
};
const recipeFilters = {
  fresh: { label: 'Fresh Recipes', content: getCompoundSentenceList },
  dry: { label: 'Baked Recipes', content: getCompoundSentenceList },
};

export const ConfirmationOrderSummary = ({
  orders,
  deliveryAddress,
  cardNumberLastFour,
  deliveryDate,
  orderTotalCost,
  orderSubtotal,
  orderTotalTax,
  orderSummaryTitle,
}) => {
  const { street, city, state, zip } = deliveryAddress;
  const starterBoxes = [];
  const subscriptions = [];

  for (let [index, order] of orders.entries()) {
    const {
      petName,
      breeds,
      price,
      discountedPrice,
      pricePerWeek,
      recipes,
    } = order;
    const { fresh, dry } = recipes;
    const count = index + 1;
    const backgroundColor = count % 2 ? 'hero.blueberry' : 'secondary.carrot';

    const startBoxSummaryList = getListItems(recipes, recipeFilters);
    const subscriptionSummaryList = getListItems(order, subscriptionfilters);

    if (fresh?.length > 0 && dry?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Mixed Bowl',
      });
    } else if (fresh?.length > 0) {
      startBoxSummaryList.unshift({ label: 'Meal Plan', content: 'All Fresh' });
    } else if (dry?.length > 0) {
      startBoxSummaryList.unshift({ label: 'Meal Plan', content: 'All Baked' });
    }

    starterBoxes.push(
      <SummaryItem
        key={`starterBox-${index}`}
        id={`starterBox-${index}`}
        title={`${petName}'s Two-week Starter Box`}
        breeds={breeds}
        backgroundColor={backgroundColor}
        summaryList={startBoxSummaryList.filter(
          ({ content }) => content !== '',
        )}
        price={price}
        discountedPrice={discountedPrice}
      />,
    );
    subscriptions.push(
      <SummaryItem
        key={`subscriptions-${index}`}
        id={`subscriptions-${index}`}
        title={`${petName}'s Subscription`}
        breeds={breeds}
        backgroundColor={backgroundColor}
        summaryList={subscriptionSummaryList}
        pricePerWeek={pricePerWeek}
      />,
    );
  }

  return (
    <StyledConfirmationOrderSummary>
      <OrderSummaryTitle>Two-week Starter Box</OrderSummaryTitle>
      {starterBoxes}
      <Section columns={2} hideBorderBottom smallMarginPadding firstMarginTop>
        <SectionSubtitle>Subtotal:</SectionSubtitle>
        <SectionSubtitle value>{orderSubtotal}</SectionSubtitle>
      </Section>
      {!!orderTotalTax && (
        <Section columns={2} hideBorderBottom smallMarginPadding>
          <SectionSubtitle>Taxes:</SectionSubtitle>
          <SectionSubtitle value>{orderTotalTax}</SectionSubtitle>
        </Section>
      )}
      <Section columns={2} smallMarginPadding lastPaddingBottom>
        <SectionTitle>Total</SectionTitle>
        <SectionTitle>{orderTotalCost}</SectionTitle>
      </Section>
      <Section>
        <SectionTitle>Billing Information</SectionTitle>
        <SectionBody>
          <strong>Card Number: </strong>**** **** **** {cardNumberLastFour}
        </SectionBody>
      </Section>
      <Section columns={2} mobileStacked>
        <SubsectionColumn left>
          <SectionTitle>Delivery Address</SectionTitle>
          <SectionBody>{street}</SectionBody>
          <SectionBody>
            {city} {state} {zip}
          </SectionBody>
        </SubsectionColumn>
        <SubsectionColumn>
          <ExpectedDeliveryWrapper>
            <SectionTitle>Expected Delivery</SectionTitle>
            <SectionBody>{deliveryDate}</SectionBody>
          </ExpectedDeliveryWrapper>
        </SubsectionColumn>
      </Section>
      <OngoingSubscriptionTitle>Ongoing Subscription</OngoingSubscriptionTitle>
      {subscriptions}
    </StyledConfirmationOrderSummary>
  );
};

ConfirmationOrderSummary.displayName = 'ConfirmationOrderSummary';
ConfirmationOrderSummary.propTypes = {
  orderSummaryTitle: PropTypes.string,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      petName: PropTypes.string.isRequired,
      breeds: PropTypes.arrayOf(PropTypes.string.isRequired),
      price: PropTypes.string.isRequired,
      discountedPrice: PropTypes.string,
      pricePerWeek: PropTypes.string.isRequired,
      frequencyInWeeks: PropTypes.number.isRequired,
      deliveryNext: PropTypes.string.isRequired,
      recipes: PropTypes.shape({
        fresh: PropTypes.arrayOf(PropTypes.string),
        dry: PropTypes.arrayOf(PropTypes.string),
      }).isRequired,
    }),
  ).isRequired,
  deliveryAddress: PropTypes.shape({
    street: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
  }).isRequired,
  cardNumberLastFour: PropTypes.string.isRequired,
  deliveryDate: PropTypes.string.isRequired,
  orderTotalCost: PropTypes.string,
  orderSubtotal: PropTypes.string,
  orderTotalTax: PropTypes.string,
};
